import axios from 'axios'

export const options = {
  "ENDPOINT": import.meta.env['VITE_API_ENDPOINT'],
  "PORT": import.meta.env['VITE_API_PORT']
}
export type IdentityServer = {
  uid: string
  name: string
  publicUrl?: string
  urls?: string[]
  /** Gateway-level: OWNER claimed the server, MEMBER joined via invite. */
  membership: string
  /** Local deployment entitlements (OWNER, ADMIN, MEMBER, …). */
  roles: string[]
  /** Gateway owner display name. */
  ownerName?: string
  /** Last 4 chars of the gateway owner userId. */
  ownerHash?: string
}

export const authState = $state({
  isLoggedIn: false,
  isServerDown: false,
  name: 'Not Connected',
  email: '',
  roles: [] as string[],
  servers: [] as IdentityServer[],
})

export function getAPIurl() {
  let url = `${window.location.protocol}//${window.location.host}${options?.ENDPOINT ?? ""}`
  if (options?.PORT) {
    url = url.replace(/\:\d{2,6}/, `:${options.PORT}`)
  }
  // console.log(url);
  return url
}

export const BASE_URL: string = getAPIurl()
export const NUREAD_GW_URL = 'https://gw.nuread.co/api'

const client = new axios.Axios({
  baseURL: BASE_URL
})

// --- Token cache -------------------------------------------------------------
// Access tokens are held only in memory (never in cookies). Only the httpOnly
// n_refresh cookie is persisted, on the home origin, and used server-side by the
// broker (POST /session/tokens). Each broker call rotates n_refresh, so the
// calls MUST be serialized to avoid racing the rotation.

type CachedToken = { token: string; exp: number }

const EXPIRY_SKEW_MS = 30_000

let gatewayToken: CachedToken | null = null
let homeSid: string | null = null
const serverTokens: Record<string, CachedToken> = {}

let mintChain: Promise<unknown> = Promise.resolve()

// Serialize broker access so concurrent callers don't race the n_refresh
// rotation. Each task re-checks the cache once it holds the lock, so redundant
// mints are coalesced. Errors are isolated so one failure doesn't poison others.
function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = mintChain.then(task, task)
  mintChain = run.then(() => {}, () => {})
  return run
}

function parseJwtExp(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (typeof payload.exp === 'number') return payload.exp * 1000
  } catch {
    // ignore malformed tokens; treat as short-lived
  }
  return Date.now() + 5 * 60_000
}

function isValid(cached: CachedToken | null | undefined): cached is CachedToken {
  return !!cached && cached.exp - EXPIRY_SKEW_MS > Date.now()
}

function cache(token: string): CachedToken {
  return { token, exp: parseJwtExp(token) }
}

async function doMint(sids: string[]): Promise<void> {
  let res: any
  try {
    res = await client.post(
      '/session/tokens',
      JSON.stringify({ sids }),
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    )
  } catch (err: any) {
    if (err?.code === 'ERR_NETWORK') authState.isServerDown = true
    throw err
  }
  if (res.status === 401) {
    authState.isLoggedIn = false
    throw new Error('unauthenticated')
  }
  if (res.status >= 400) {
    throw new Error(`mint failed: ${res.status}`)
  }
  const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data
  if (data.gateway) gatewayToken = cache(data.gateway)
  if (data.home) homeSid = data.home
  for (const [sid, token] of Object.entries(data.tokens ?? {})) {
    serverTokens[sid] = cache(token as string)
  }
  authState.isLoggedIn = true
  authState.isServerDown = false
}

// Warm the cache with per-server tokens for the given servers (single batch).
export async function primeServerTokens(sids: string[]): Promise<void> {
  await enqueue(async () => {
    const missing = sids.filter((sid) => !isValid(serverTokens[sid]))
    if (missing.length === 0) return
    try {
      await doMint(missing)
    } catch {
      // best-effort warm-up; individual calls will re-mint on demand
    }
  })
}

// Gateway-scoped token (aud includes gateway) for calls to the central gateway.
export async function getGatewayToken(): Promise<string | undefined> {
  if (isValid(gatewayToken)) return gatewayToken!.token
  await enqueue(async () => {
    if (isValid(gatewayToken)) return
    try {
      await doMint([])
    } catch {
      // surfaced to caller via the null token below
    }
  })
  return isValid(gatewayToken) ? gatewayToken!.token : undefined
}

// Per-server token (aud=[sid, nuread-dev]) for calls to a deployment API.
export async function getServerToken(sid: string): Promise<string | undefined> {
  if (isValid(serverTokens[sid])) return serverTokens[sid].token
  await enqueue(async () => {
    if (isValid(serverTokens[sid])) return
    try {
      await doMint([sid])
    } catch {
      // surfaced to caller via the null token below
    }
  })
  return isValid(serverTokens[sid]) ? serverTokens[sid].token : undefined
}

export function invalidateServerToken(sid: string) {
  delete serverTokens[sid]
}

export function getHomeSid(): string | null {
  return homeSid
}

// --- Session lifecycle -------------------------------------------------------

export function logout() {
  authState.isLoggedIn = false
  authState.name = 'Not Connected'
  authState.email = ''
  authState.roles = []
  authState.servers = []
  gatewayToken = null
  homeSid = null
  for (const sid of Object.keys(serverTokens)) delete serverTokens[sid]
  return axios.post(`${BASE_URL}/logout`, {}, { withCredentials: true })
}

// Establishes a session by minting a gateway token from the httpOnly refresh
// cookie. Returns true if the user is authenticated.
export async function validateSession(): Promise<boolean> {
  const token = await getGatewayToken()
  return !!token
}

export async function getIdentity() {
  try {
    // Mint before identity: the SPA only has n_refresh until /session/tokens
    // runs. Calling identity first (with no bearer) 401s and never retries.
    await getGatewayToken()
    const sid = homeSid
    if (!sid) {
      console.log('getIdentity Failed: no home sid after mint')
      return
    }
    const token = await getServerToken(sid)
    if (!token) {
      console.log('getIdentity Failed: no server token for home')
      return
    }

    const res = await client.get('/v1/auth/identity', {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.status == 401) {
      throw new Error("unauthenticated");
    }
    const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data
    authState.name = data.name
    authState.email = data.email
    authState.roles = Array.isArray(data.roles) ? data.roles : []
    // Servers + per-deployment roles are loaded via refreshServerAccess (gateway
    // membership + each deployment's local entitlements).
    if (Array.isArray(data.servers) && authState.servers.length === 0) {
      authState.servers = data.servers.map((s: any) => ({
        uid: s.uid,
        name: s.name,
        publicUrl: s.publicUrl,
        urls: s.urls,
        membership: String(s.membership ?? 'MEMBER').toUpperCase(),
        roles: Array.isArray(s.roles) ? s.roles : [],
        ownerName: s.ownerName,
        ownerHash: s.ownerHash,
      }))
    }
    return data
  } catch (err) {
    if (String(err).indexOf("unauthenticated") != -1) {
      console.log("getIdentity Failed")
      return
    }
    throw err;
  }
}
